import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { LoadingButton } from '@mui/lab';
import { Box, Button, Typography } from '@mui/material';
import { Mail, ArrowBack } from '@mui/icons-material';
import { yupResolver } from '@hookform/resolvers/yup';

import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { client } from '@frontend/features/common/services';
import { theme } from '@frontend/theme';

import { ControlledTextField } from '../../common/components/ControlledTextField';
import { AuthScreens } from '../types';

interface ForgotPasswordProps {
  onChangeScreen: (nextPage: AuthScreens) => (email?: string) => void;
}

const resolver = yupResolver(
  yup.object().shape({
    email: yup.string().required(),
  }),
);

export const ForgotPassword = ({ onChangeScreen }: ForgotPasswordProps) => {
  const { t } = useTranslation();
  const { addToast } = useSnackBar();
  const { handleSubmit, control } = useForm({ resolver });
  const [isLoading, setIsLoading] = useState(false);

  const onSubmit = async ({ email }: { email: string }) => {
    try {
      setIsLoading(true);
      await client.post('auth/forgot-password', { email });
      addToast({
        message: t('auth.forgotPassword.sentEmail'),
        color: 'success',
      });
      onChangeScreen('code-reset')(email);
    } catch (_error) {
      addToast({
        message: t('auth.forgotPassword.error'),
        color: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        mt: theme.spacing(6),
      }}
    >
      <Typography variant="h6" textAlign="center">
        {t('auth.forgotPassword.title')}
      </Typography>
      <Typography variant="caption" textAlign="center" sx={{ width: 350 }}>
        {t('auth.forgotPassword.subtitle')}
      </Typography>

      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(2),
          mt: theme.spacing(3),
          width: '100%',
        }}
      >
        <ControlledTextField
          required
          fullWidth
          control={control}
          name="email"
          type="email"
          label={t('auth.forgotPassword.email')}
          errorMessage={t('common.form.required')}
          InputProps={{
            startAdornment: <Mail color="primary" sx={{ mx: theme.spacing(1) }} />,
          }}
        />
        <LoadingButton
          fullWidth
          size="medium"
          color="primary"
          variant="contained"
          type="submit"
          loading={isLoading}
          sx={{
            borderRadius: theme.spacing(1),
            marginTop: theme.spacing(2),
          }}
        >
          <Typography
            sx={{ letterSpacing: '0.4px', fontWeight: (theme) => theme.typography.fontWeightMedium }}
            variant="subtitle2"
          >
            {t('auth.forgotPassword.reset')}
          </Typography>
        </LoadingButton>
        <Button startIcon={<ArrowBack />} variant="outlined" onClick={() => onChangeScreen('login')()}>
          <Typography
            sx={{ letterSpacing: '0.4px', fontWeight: (theme) => theme.typography.fontWeightMedium }}
            variant="subtitle2"
          >
            {t('auth.forgotPassword.back')}
          </Typography>
        </Button>
      </Box>
    </Box>
  );
};
